import type { SubmitHandler } from 'react-hook-form';
import { useState } from 'react';
import { faFolder } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiCreateCategoryResponseBody } from '~/lib/api/modules/Categories';
import type { IUseCreateCategoryMutationValues } from '~/lib/api/modules/Categories/hooks/useCreateCategoryMutation';
import type { ILinkFormValues } from './LinkForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useCreateCategoryMutation } from '~/lib/api/modules/Categories/hooks/useCreateCategoryMutation';
import { LinkForm } from './LinkForm';

export type ICreateLinkFormProps = IContainerProps & {
  onSuccess?: (
    outputData: IApiCreateCategoryResponseBody,
    inputValues: IUseCreateCategoryMutationValues,
  ) => void;
  cancelUrl?: string;
  appId: string;
  parentCategoryId?: string;
};

export const CreateLinkForm: React.FC<ICreateLinkFormProps> = (props) => {
  const { sx, onSuccess, cancelUrl, appId, parentCategoryId } = props;
  const createLinkMutation = useCreateCategoryMutation();
  const { t } = useTranslation();
  const [isDraft, setIsDraft] = useState(false);

  const handleSubmit: SubmitHandler<ILinkFormValues> = (values) => {
    if (createLinkMutation.isLoading) {
      return;
    }

    return createLinkMutation.mutateAsync(
      {
        appId,
        body: {
          name: values.name ?? '',
          picture: values.pictures?.length ? [values.pictures[0].id] : [],
          parentId: values.parentId ? values.parentId : undefined,
          action: `${values.actionPrefix ?? ''}${values.actionTarget}`,
          hidden: values.isDraft ?? false,
          isEvent: false,
          reversedFlow: false,
          badges: values.userBadges,
          badgesAllow: values.requireAllUserBadges ? 'all' : 'any',
        },
      },
      { onSuccess },
    );
  };

  return (
    <ContentBlock draft={isDraft} sx={sx} draftOutlineSize="lg">
      <ContentBlock.Title icon={<FontAwesomeIcon icon={faFolder} />}>
        {t('CreateLinkForm.title')}{' '}
        {isDraft ? t('CreateArticleForm.not-published-yet') : null}
      </ContentBlock.Title>

      <LinkForm
        mode="creation"
        appId={appId}
        cancelUrl={cancelUrl}
        defaultValues={{ parentId: parentCategoryId }}
        onSubmit={handleSubmit}
        onDraftStatusChange={setIsDraft}
      />
    </ContentBlock>
  );
};
